import React, { useCallback, useMemo, useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  strapiListInvoiceGridRows,
  strapiRunInvoiceWorkOrderAction,
  type InvoiceGridSort,
  type InvoiceTestingStatus,
  type InvoiceWorkflowAction,
  type StrapiInvoiceGridRow,
} from '../../../../api/strapiCrm';
import { useAuth } from '../../../../auth/AuthContext';
import {
  DataGridNumberedPagination,
  DATA_GRID_DEFAULT_PAGE_SIZE,
  DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS,
} from '../../../../components/DataGridNumberedPagination';
import { queryKeys } from '../../../../query/queryKeys';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';
import { commitListFiltersOnEnter } from '../../../../ui/listFiltersApi';
import {
  formatInvoiceRequestDate,
  TESTING_STATUS_LABEL,
} from '../../../InvoicesPage/invoiceGridLabels';
import { LhuPdfPreviewDialog } from '../../../InvoicesPage/LhuPdfPreviewDialog';
import { HasilUjiLetterViewDialog } from '../../components/HasilUjiLetterViewDialog';

const LIST_SORT: InvoiceGridSort = 'quotation_request_date:desc';

const STATUS_FILTER_ALL = '';
const STATUS_FILTER_OPTIONS: InvoiceTestingStatus[] = [
  'pengujian',
  'verifikasi',
  'draft_lhu',
  'verifikasi_lhu',
  'cetak',
  'revisi_lhu',
];

type HasilUjiLetterFilters = {
  jobNumber: string;
  releasedFrom: string;
  releasedTo: string;
  status: InvoiceTestingStatus | typeof STATUS_FILTER_ALL;
};

const EMPTY_FILTERS: HasilUjiLetterFilters = {
  jobNumber: '',
  releasedFrom: '',
  releasedTo: '',
  status: STATUS_FILTER_ALL,
};

function filtersActive(f: HasilUjiLetterFilters): boolean {
  return Boolean(f.jobNumber.trim() || f.releasedFrom || f.releasedTo || f.status);
}

/** Local calendar day `YYYY-MM-DD` so it compares with `<input type="date">`. */
function localDayKey(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function formatReleasedAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('id-ID');
}

function matchesFilters(row: StrapiInvoiceGridRow, f: HasilUjiLetterFilters): boolean {
  const job = f.jobNumber.trim().toLowerCase();
  if (job && !row.job_number.toLowerCase().includes(job)) return false;
  if (f.status && row.testing_status !== f.status) return false;
  if (f.releasedFrom || f.releasedTo) {
    const day = localDayKey(row.lhu_released_at);
    if (!day) return false;
    if (f.releasedFrom && day < f.releasedFrom) return false;
    if (f.releasedTo && day > f.releasedTo) return false;
  }
  return true;
}

const TESTING_CHIP_COLOR: Record<
  InvoiceTestingStatus,
  'info' | 'warning' | 'default' | 'secondary' | 'success' | 'error'
> = {
  pengujian: 'info',
  verifikasi: 'warning',
  draft_lhu: 'default',
  verifikasi_lhu: 'secondary',
  cetak: 'success',
  revisi_lhu: 'error',
  lhu_rilis: 'success',
};

type GridAction =
  | 'approve_gl'
  | 'verify_lhu'
  | 'approve_manajer_teknis'
  | 'customer_approve_lhu'
  | 'reject_gl'
  | 'revisi_lhu';

function ActionIcon({
  title,
  ariaLabel,
  testId,
  disabled,
  color,
  onClick,
  children,
}: {
  title: string;
  ariaLabel: string;
  testId: string;
  disabled?: boolean;
  color?: 'default' | 'success' | 'error' | 'primary';
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const button = (
    <IconButton
      size="small"
      color={color}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </IconButton>
  );
  return <Tooltip title={title}>{disabled ? <span>{button}</span> : button}</Tooltip>;
}

function approveActionForRow(row: StrapiInvoiceGridRow): GridAction | null {
  if (row.actions.customer_approve_lhu) return 'customer_approve_lhu';
  if (row.actions.approve_manajer_teknis) return 'approve_manajer_teknis';
  if (row.actions.verify_lhu) return 'verify_lhu';
  if (row.actions.approve_gl) return 'approve_gl';
  return null;
}

function approveTitle(action: GridAction | null): string {
  if (action === 'customer_approve_lhu') return 'Approve (Customer)';
  if (action === 'approve_manajer_teknis') return 'Approve Manajer Teknis';
  if (action === 'verify_lhu') return 'Approve SPV Admin LHU';
  if (action === 'approve_gl') return 'Approve LHU';
  return 'Approve';
}

/** Approval hierarchy always shown; green = approved, grey = pending. */
const APPROVAL_HIERARCHY: Array<{
  key: keyof Pick<
    StrapiInvoiceGridRow,
    | 'approved_by_spv_lab'
    | 'approved_by_spv_admin_sampling'
    | 'approved_by_spv_admin_lhu'
    | 'approved_by_manajer_teknis'
  >;
  label: string;
}> = [
  { key: 'approved_by_spv_lab', label: 'SPV lab' },
  { key: 'approved_by_spv_admin_sampling', label: 'SPV Admin Sampling' },
  { key: 'approved_by_spv_admin_lhu', label: 'SPV Admin LHU' },
  { key: 'approved_by_manajer_teknis', label: 'Manajer Teknis' },
];

export function HasilUjiLetterListTabScreen() {
  const { jwt, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{
    action: GridAction;
    row: StrapiInvoiceGridRow;
  } | null>(null);
  const [revisiNote, setRevisiNote] = useState('');
  const [viewRow, setViewRow] = useState<StrapiInvoiceGridRow | null>(null);
  const [previewWoDocId, setPreviewWoDocId] = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState('');
  const [previewCustomerEmail, setPreviewCustomerEmail] = useState('');
  const [canSendCustomerEmail, setCanSendCustomerEmail] = useState(false)

  const userType = user?.userTypeCode?.trim().toLowerCase() ?? '';
  const isSpvAdminSampling = userType === 'spv_admin_sampling';
  const isSpvLaboratorium = userType === 'spv_laboratorium';
  const isSpvAdminLhu = userType === 'spv_admin_lhu' || userType === 'admin_lhu';
  const isManajerTeknis = userType === 'manajer_teknis';
  const isCustomerService = userType === 'customer_service';
  const isSuperadmin = userType === 'superadmin';
  const viewHasilUjiOnly =
    isSpvAdminSampling || isSpvAdminLhu || isManajerTeknis || isCustomerService;
  const canShowGridApprove =
    isSpvAdminSampling || isSpvAdminLhu || isManajerTeknis || isCustomerService || isSuperadmin;

  const openPreview = useCallback((row: StrapiInvoiceGridRow) => {
    const canSend =
      isCustomerService &&
      (row.testing_status === 'verifikasi_lhu' || row.testing_status === 'cetak');
    setCanSendCustomerEmail(canSend);
    setPreviewWoDocId(row.work_order_document_id);
    setPreviewLabel([row.job_number, row.quotation_number].filter(Boolean).join(' · '));
    setPreviewCustomerEmail(row.customer_email);
  }, [isCustomerService]);

  const rowsQ = useQuery({
    queryKey: queryKeys.invoiceGridRows(jwt, LIST_SORT),
    queryFn: () => strapiListInvoiceGridRows(jwt!, LIST_SORT),
    enabled: Boolean(jwt),
  });

  const actionMut = useMutation({
    mutationFn: ({
      workOrderDocumentId,
      action,
      revisionNote,
    }: {
      workOrderDocumentId: string;
      action: Exclude<InvoiceWorkflowAction, 'preview_lhu' | 'preview_invoice' | 'preview_proforma'>;
      revisionNote?: string;
    }) =>
      strapiRunInvoiceWorkOrderAction(
        jwt!,
        workOrderDocumentId,
        action,
        revisionNote != null ? { revision_note: revisionNote } : undefined,
      ),
    onSuccess: async (_row, vars) => {
      setActionError(null);
      const msg =
        vars.action === 'approve_gl'
          ? 'Approve LHU berhasil. Status menjadi Draft LHU.'
          : vars.action === 'verify_lhu' || vars.action === 'approve_manajer_teknis'
            ? 'Approve Draft LHU berhasil.'
            : vars.action === 'customer_approve_lhu'
              ? 'Approve Customer Service berhasil. Status menjadi Cetak.'
              : vars.action === 'revisi_lhu'
                ? 'Revisi LHU berhasil. Status kembali ke Pengujian.'
                : 'Request edit berhasil.';
      setActionSuccess(msg);
      setConfirm(null);
      setRevisiNote('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoiceGridRows(jwt) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.laboratoryResultGridRowsPrefix(jwt) });
    },
    onError: (e) => {
      setActionSuccess(null);
      setActionError(e instanceof Error ? e.message : 'Gagal menjalankan aksi.');
    },
  });

  const [draftFilters, setDraftFilters] = useState<HasilUjiLetterFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<HasilUjiLetterFilters>(EMPTY_FILTERS);

  const commitFilters = useCallback(() => {
    setAppliedFilters({ ...draftFilters });
  }, [draftFilters]);

  const applyFilterNow = useCallback((patch: Partial<HasilUjiLetterFilters>) => {
    setDraftFilters((d) => ({ ...d, ...patch }));
    setAppliedFilters((a) => ({ ...a, ...patch }));
  }, []);

  const resetFilters = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  }, []);

  const allRows = useMemo(() => rowsQ.data ?? [], [rowsQ.data]);
  const rows = useMemo(
    () => allRows.filter((row) => matchesFilters(row, appliedFilters)),
    [allRows, appliedFilters],
  );
  const appliedActive = filtersActive(appliedFilters);

  const columns: GridColDef<StrapiInvoiceGridRow>[] = useMemo(
    () => [
      {
        field: 'quotation_number',
        headerName: 'No quotation',
        minWidth: 140,
        flex: 1,
        valueGetter: (_, row) => row.quotation_number || '—',
      },
      {
        field: 'job_number',
        headerName: 'Job number',
        minWidth: 130,
        flex: 0.9,
        valueGetter: (_, row) => row.job_number || '—',
      },
      {
        field: 'quotation_request_date',
        headerName: 'Tanggal permintaan quotation',
        minWidth: 200,
        flex: 1,
        valueGetter: (_, row) => formatInvoiceRequestDate(row.quotation_request_date),
      },
      {
        field: 'testing_status',
        headerName: 'Status pengujian',
        minWidth: 160,
        flex: 0.9,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={TESTING_STATUS_LABEL[row.testing_status] ?? row.testing_status}
            color={TESTING_CHIP_COLOR[row.testing_status] ?? 'default'}
          />
        ),
      },
      {
        field: 'lhu_released_at',
        headerName: 'Tanggal rilis LHU',
        minWidth: 140,
        flex: 0.8,
        valueGetter: (_, row) => row.lhu_released_at ?? '',
        renderCell: ({ row }) => formatReleasedAt(row.lhu_released_at),
      },
      {
        field: 'approvals',
        headerName: 'Approval',
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        valueGetter: (_, row) =>
          APPROVAL_HIERARCHY.map(
            (item) => `${item.label}:${row[item.key] ? 'approved' : 'pending'}`,
          ).join('|'),
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0.5,
              width: '100%',
              py: 0.75,
            }}
          >
            {APPROVAL_HIERARCHY.map((item, index) => {
              const approved = row[item.key] === true;
              return (
                <Box
                  key={item.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    pl: index * 1.25,
                  }}
                >
                  {index > 0 ? (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1, userSelect: 'none' }}
                    >
                      └
                    </Typography>
                  ) : null}
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: approved ? 'success.main' : 'grey.400',
                      color: approved ? 'common.white' : 'grey.800',
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: 1.4,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Aksi',
        width: 260,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const woKey = row.job_number || row.work_order_document_id;
          const approveAction = approveActionForRow(row);
          const canApprove = Boolean(approveAction);
          const showApproveHint =
            canShowGridApprove &&
            row.testing_status === 'pengujian' &&
            !row.approved_by_spv_lab &&
            (isSpvAdminSampling || isSuperadmin);
          return (
            <Box
              sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {row.actions.preview_lhu ? (
                <ActionIcon
                  title="Preview LHU"
                  ariaLabel={`Preview LHU ${woKey}`}
                  testId={`hasil-uji-letter-preview-lhu-${row.work_order_document_id}`}
                  onClick={() => openPreview(row)}
                >
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                </ActionIcon>
              ) : null}
              {isSpvAdminLhu && row.testing_status === 'draft_lhu' && row.actions.preview_lhu ? (
                <ActionIcon
                  title="Edit konten LHU"
                  ariaLabel={`Edit konten LHU ${woKey}`}
                  testId={`hasil-uji-letter-edit-lhu-html-${row.work_order_document_id}`}
                  onClick={() => {
                    const job = row.job_number.trim();
                    if (!job) return;
                    navigate(`/sampling/hasil-uji-letter/${encodeURIComponent(job)}`);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </ActionIcon>
              ) : null}
              {canApprove || showApproveHint ? (
                <ActionIcon
                  title={
                    canApprove
                      ? approveTitle(approveAction)
                      : `Approve LHU terkunci: ${row.parameters_review.approved_count}/${row.parameters_review.expected_count} parameter approved SPV lab`
                  }
                  ariaLabel={`${approveTitle(approveAction)} ${woKey}`}
                  testId={`hasil-uji-letter-approve-${row.work_order_document_id}`}
                  color="success"
                  disabled={!canApprove}
                  onClick={() => {
                    if (!approveAction) return;
                    setActionError(null);
                    setActionSuccess(null);
                    setConfirm({ action: approveAction, row });
                  }}
                >
                  <CheckCircleOutlineIcon fontSize="small" />
                </ActionIcon>
              ) : null}
              <ActionIcon
                title={viewHasilUjiOnly ? 'View hasil lab (read-only)' : 'View hasil lab'}
                ariaLabel={`View hasil lab ${woKey}`}
                testId={`hasil-uji-letter-view-${row.work_order_document_id}`}
                onClick={() => setViewRow(row)}
              >
                <ScienceOutlinedIcon fontSize="small" />
              </ActionIcon>
              {row.actions.reject_gl ? (
                <ActionIcon
                  title="Request edit"
                  ariaLabel={`Request edit ${woKey}`}
                  testId={`hasil-uji-letter-request-edit-${row.work_order_document_id}`}
                  color="error"
                  onClick={() => {
                    setActionError(null);
                    setActionSuccess(null);
                    setConfirm({ action: 'reject_gl', row });
                  }}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </ActionIcon>
              ) : null}
              {row.actions.revisi_lhu ? (
                <ActionIcon
                  title="Revisi LHU"
                  ariaLabel={`Revisi LHU ${woKey}`}
                  testId={`hasil-uji-letter-revisi-lhu-${row.work_order_document_id}`}
                  color="error"
                  onClick={() => {
                    setActionError(null);
                    setActionSuccess(null);
                    setRevisiNote('');
                    setConfirm({ action: 'revisi_lhu', row });
                  }}
                >
                  <CancelOutlinedIcon fontSize="small" />
                </ActionIcon>
              ) : null}
            </Box>
          );
        },
      },
    ],
    [
      canShowGridApprove,
      isSpvAdminLhu,
      isSpvAdminSampling,
      isSuperadmin,
      openPreview,
      viewHasilUjiOnly,
    ],
  );

  const confirmTitle =
    confirm?.action === 'reject_gl'
      ? 'Request edit?'
      : confirm?.action === 'revisi_lhu'
        ? 'Revisi LHU?'
        : confirm?.action === 'customer_approve_lhu'
          ? 'Approve Customer?'
          : confirm?.action === 'approve_manajer_teknis'
            ? 'Approve Manajer Teknis?'
            : confirm?.action === 'verify_lhu'
              ? 'Approve SPV Admin LHU?'
              : 'Approve LHU?';

  const confirmBody =
    confirm?.action === 'approve_gl'
      ? `Setujui hasil uji untuk ${confirm.row.job_number || confirm.row.work_order_document_id}? Status menjadi Draft LHU.`
      : confirm?.action === 'verify_lhu'
        ? `Approve sebagai SPV Admin LHU untuk ${confirm.row.job_number}? Status naik ke Verifikasi LHU setelah Manajer Teknis juga approve.`
        : confirm?.action === 'approve_manajer_teknis'
          ? `Approve sebagai Manajer Teknis untuk ${confirm.row.job_number}? Status naik ke Verifikasi LHU setelah SPV Admin LHU juga approve.`
          : confirm?.action === 'customer_approve_lhu'
            ? `Customer menyetujui draft LHU untuk ${confirm.row.job_number}? Status menjadi Cetak.`
            : confirm?.action === 'revisi_lhu'
              ? `Minta revisi LHU untuk ${confirm?.row.job_number}? Status kembali ke Pengujian.`
              : `Tolak hasil uji untuk ${confirm?.row.job_number}? Status kembali ke Pengujian.`;

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CollapsibleListFilters
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={rowsQ.isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={!jwt || rowsQ.isFetching}
            onClick={() => void rowsQ.refetch()}
            data-testid="hasil-uji-letter-refresh-button"
          >
            Muat ulang
          </Button>
        }
        filterToggleTestId="hasil-uji-letter-filter-toggle"
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="Job number"
            value={draftFilters.jobNumber}
            onChange={(e) => setDraftFilters((d) => ({ ...d, jobNumber: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'hasil-uji-letter-filter-job-number' }}
          />
          <TextField
            label="Rilis LHU dari"
            type="date"
            value={draftFilters.releasedFrom}
            onChange={(e) => applyFilterNow({ releasedFrom: e.target.value })}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: draftFilters.releasedTo || undefined,
              'data-testid': 'hasil-uji-letter-filter-released-from',
            }}
          />
          <TextField
            label="Rilis LHU sampai"
            type="date"
            value={draftFilters.releasedTo}
            onChange={(e) => applyFilterNow({ releasedTo: e.target.value })}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: draftFilters.releasedFrom || undefined,
              'data-testid': 'hasil-uji-letter-filter-released-to',
            }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="hasil-uji-letter-filter-status-label">Status LHU</InputLabel>
            <Select
              labelId="hasil-uji-letter-filter-status-label"
              label="Status LHU"
              value={draftFilters.status}
              onChange={(e) =>
                applyFilterNow({ status: e.target.value as HasilUjiLetterFilters['status'] })
              }
              data-testid="hasil-uji-letter-filter-status"
            >
              <MenuItem value={STATUS_FILTER_ALL}>Semua status</MenuItem>
              {STATUS_FILTER_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {TESTING_STATUS_LABEL[status] ?? status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {appliedActive
              ? `${rows.length} dari ${allRows.length} work order`
              : `${allRows.length} work order`}
          </Typography>
          <Button
            size="small"
            color="inherit"
            disabled={!appliedActive && !filtersActive(draftFilters)}
            onClick={resetFilters}
            data-testid="hasil-uji-letter-filter-reset"
          >
            Reset filter
          </Button>
        </Stack>
      </CollapsibleListFilters>

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      {actionSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      ) : null}

      {rowsQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {rowsQ.error instanceof Error ? rowsQ.error.message : 'Gagal memuat daftar.'}
        </Alert>
      ) : null}

      {!rowsQ.isPending && !rowsQ.isFetching && allRows.length === 0 && !rowsQ.isError ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Belum ada work order untuk review. Baris muncul setelah sample diterima kantor dan masuk
          alur pengujian.
        </Alert>
      ) : null}

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.row_key}
        getRowHeight={() => 'auto'}
        loading={rowsQ.isPending || rowsQ.isFetching}
        disableRowSelectionOnClick
        pageSizeOptions={DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS}
        initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
        slots={{ pagination: DataGridNumberedPagination }}
        sx={{
          flex: 1,
          minHeight: 360,
          '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 0.5 },
        }}
      />

      <Dialog
        open={Boolean(confirm)}
        onClose={() => {
          if (!actionMut.isPending) {
            setConfirm(null);
            setRevisiNote('');
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: confirm?.action === 'revisi_lhu' ? 2 : 0 }}>
            {confirmBody}
          </Typography>
          {confirm?.action === 'revisi_lhu' ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label="Catatan revisi"
              value={revisiNote}
              onChange={(e) => setRevisiNote(e.target.value)}
              data-testid="hasil-uji-letter-revisi-note"
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={actionMut.isPending}
            onClick={() => {
              setConfirm(null);
              setRevisiNote('');
            }}
          >
            Batal
          </Button>
          <Button
            variant="contained"
            color={
              confirm?.action === 'reject_gl' || confirm?.action === 'revisi_lhu' ? 'error' : 'primary'
            }
            disabled={
              actionMut.isPending ||
              !confirm ||
              (confirm.action === 'revisi_lhu' && !revisiNote.trim())
            }
            onClick={() => {
              if (!confirm) return;
              actionMut.mutate({
                workOrderDocumentId: confirm.row.work_order_document_id,
                action: confirm.action,
                revisionNote:
                  confirm.action === 'revisi_lhu' ? revisiNote.trim() : undefined,
              });
            }}
          >
            {actionMut.isPending ? 'Memproses…' : 'Ya, lanjutkan'}
          </Button>
        </DialogActions>
      </Dialog>

      <HasilUjiLetterViewDialog
        open={Boolean(viewRow)}
        workOrderDocumentId={viewRow?.work_order_document_id ?? ''}
        jobNumber={viewRow?.job_number}
        viewOnly={viewHasilUjiOnly}
        showFieldParameter={isSpvAdminSampling || isSpvLaboratorium}
        onClose={() => setViewRow(null)}
      />

      <LhuPdfPreviewDialog
        open={Boolean(previewWoDocId)}
        workOrderDocumentId={previewWoDocId}
        workOrderLabel={previewLabel}
        jwt={jwt}
        canSendCustomerEmail={canSendCustomerEmail}
        customerEmail={previewCustomerEmail}
        onClose={() => {
          setPreviewWoDocId(null);
          setPreviewLabel('');
          setPreviewCustomerEmail('');
        }}
      />

    </Box>
  );
}
